import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  AlertTriangle,
  RefreshCw,
  ShoppingBag,
  Users,
  CreditCard,
  Building2,
  HandCoins,
} from 'lucide-react';
import { PageShell } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { useHasRole } from '@/providers/AuthProvider';
import { VENDOR_ROUTE_ROLES } from '@/features/vendor/lib/vendorActionRbac';
import { FUNDING_ROUTE_ROLES } from '@/features/funding/lib/fundingActionRbac';
import { KpiHeroCard, type KpiHeroTone } from '@/features/dashboard/components/KpiHeroCard';
import { KpiSecondaryCard, type KpiSecondaryTone } from '@/features/dashboard/components/KpiSecondaryCard';
import {
  DashboardRangeFilter,
  type DashboardRange,
} from '@/features/dashboard/components/DashboardRangeFilter';
import { RevenueHeroPanel } from '@/features/dashboard/components/RevenueHeroPanel';
import { ChannelDonutChart } from '@/features/dashboard/components/ChannelDonutChart';
import { ActivityFeed } from '@/features/dashboard/components/ActivityFeed';
import { LowStockList } from '@/features/dashboard/components/LowStockList';
import { TopSellingList } from '@/features/dashboard/components/TopSellingList';
import { QuickActionsPanel } from '@/features/dashboard/components/QuickActionsPanel';
import { useDashboardKpiQuery } from '@/features/dashboard/hooks/useDashboardKpiQuery';
import { useDashboardWidgetQuery } from '@/features/dashboard/hooks/useDashboardWidgetQuery';

const formatCount = (count: number | null | undefined): string => {
  if (count == null) return '—';
  return new Intl.NumberFormat('ko-KR').format(count);
};

const formatKRWCompact = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return new Intl.NumberFormat('ko-KR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

const ORDERS_PENDING_QUERY = { orderStatus: 'CANCEL_REQUESTED' } as const;
// 미정산 결제 — 고도몰 이관 결제(약 60만 건, 고도몰 PG 에서 이미 정산 완료)는 빼고 에버라인 신규 결제만 센다.
// 카드 링크도 같은 조건으로 결제 목록을 연다(목록이 URL 을 읽어 "이관 결제 제외" 를 체크한다).
const PAYMENTS_PENDING_QUERY = { paymentStatus: 'CAPTURED', excludeLegacy: true } as const;
const PAYMENTS_PENDING_LINK = `/payments?paymentStatus=${PAYMENTS_PENDING_QUERY.paymentStatus}&excludeLegacy=true`;
const CLAIMS_PENDING_QUERY = { claimStatus: 'REQUESTED' } as const;
// r5: vendor 입점신청 심사 대기 = 운영자 액션 필요 상태(VendorFiltersBar 와 동일 기준).
const VENDOR_PENDING_QUERY = {
  status: ['SUBMITTED', 'UNDER_REVIEW', 'RESUBMISSION_REQUESTED'],
} as const;
// r5: funding 진행 중 캠페인 = ACTIVE.
const FUNDING_ACTIVE_QUERY = { status: ['ACTIVE'] } as const;

interface HeroKpi {
  label: string;
  value: string;
  unit?: string | undefined;
  deltaPercent?: number | null | undefined;
  deltaLabel?: string | undefined;
  sparkline?: ReadonlyArray<{ label: string; value: number }> | undefined;
  tone: KpiHeroTone;
  to?: string | undefined;
  hint?: string | undefined;
  testId: string;
  icon: React.ReactNode;
}

interface SecondaryKpi {
  label: string;
  value: string;
  tone: KpiSecondaryTone;
  to?: string | undefined;
  hint?: string | undefined;
  testId: string;
  icon: React.ReactNode;
}

export const DashboardPage = () => {
  const { t } = useTranslation('common');
  const [params] = useSearchParams();
  const denied = params.get('denied') === '1';
  const [range, setRange] = useState<DashboardRange>('7d');

  // Pending-status KPI counts (per-domain list endpoints with size=1)
  const ordersKpi = useDashboardKpiQuery('orders', ORDERS_PENDING_QUERY);
  const paymentsKpi = useDashboardKpiQuery('payments', PAYMENTS_PENDING_QUERY);
  const claimsKpi = useDashboardKpiQuery('claims', CLAIMS_PENDING_QUERY);

  // r5: vendor / funding KPI — RBAC 가드(해당 role만 노출 + 쿼리도 비활성화).
  const canViewVendors = useHasRole(VENDOR_ROUTE_ROLES);
  const canViewFunding = useHasRole(FUNDING_ROUTE_ROLES);
  const vendorsKpi = useDashboardKpiQuery('vendors', VENDOR_PENDING_QUERY, {
    enabled: canViewVendors,
  });
  const fundingKpi = useDashboardKpiQuery('funding', FUNDING_ACTIVE_QUERY, {
    enabled: canViewFunding,
  });

  // Aggregated dashboard widget (summary + chart + recents)
  const widgetQuery = useDashboardWidgetQuery(range);
  const summary = widgetQuery.data?.summary;
  const daily = widgetQuery.data?.revenueChart?.daily ?? [];

  const revenueSparkline = daily.map((d) => ({
    label: d.date ?? '',
    value: d.amount ?? 0,
  }));
  const orderSparkline = daily.map((d) => ({
    label: d.date ?? '',
    value: d.orderCount ?? 0,
  }));

  const heroKpis: ReadonlyArray<HeroKpi> = [
    {
      label: t('dashboard.heroKpis.totalRevenue.label'),
      value: formatKRWCompact(summary?.totalRevenue),
      unit: '원',
      deltaPercent: summary?.revenueChangeRate ?? null,
      deltaLabel: t('dashboard.heroKpis.totalRevenue.deltaLabel'),
      sparkline: revenueSparkline,
      tone: 'neutral',
      to: PAYMENTS_PENDING_LINK,
      hint: t('dashboard.heroKpis.totalRevenue.hint'),
      testId: 'dashboard-hero-revenue',
      icon: <CreditCard className="h-4 w-4" />,
    },
    {
      label: t('dashboard.heroKpis.totalOrders.label'),
      value: formatCount(summary?.totalOrders),
      unit: '건',
      sparkline: orderSparkline,
      tone: 'info',
      to: '/orders',
      hint: t('dashboard.heroKpis.totalOrders.hint'),
      testId: 'dashboard-hero-orders',
      icon: <ShoppingBag className="h-4 w-4" />,
    },
    {
      label: t('dashboard.heroKpis.newCustomers.label'),
      value: formatCount(summary?.newCustomers),
      unit: '명',
      tone: 'success',
      to: '/users/members',
      hint: t('dashboard.heroKpis.newCustomers.hint'),
      testId: 'dashboard-hero-newCustomers',
      icon: <Users className="h-4 w-4" />,
    },
    {
      label: t('dashboard.heroKpis.lowStock.label'),
      value: formatCount(summary?.lowStockCount),
      unit: '품목',
      tone: (summary?.lowStockCount ?? 0) > 0 ? 'danger' : 'neutral',
      hint: t('dashboard.heroKpis.lowStock.hint'),
      testId: 'dashboard-hero-lowStock',
      icon: <AlertTriangle className="h-4 w-4" />,
    },
  ];

  // 처리 대기 액션 KPI — 운영자가 즉시 처리해야 하는 큐.
  // vendor/funding 은 r5 오픈 기능으로 RBAC 가드(해당 role만) 후 노출.
  const secondaryKpis: ReadonlyArray<SecondaryKpi> = [
    ...(canViewVendors
      ? [
          {
            label: t('dashboard.kpis.vendors.label'),
            value: formatCount(vendorsKpi.data?.totalItems),
            tone: 'warn' as const,
            to: '/vendors',
            hint: t('dashboard.kpis.vendors.hint'),
            testId: 'dashboard-kpi-vendors',
            icon: <Building2 className="h-4 w-4" />,
          },
        ]
      : []),
    {
      label: t('dashboard.kpis.orders.label'),
      value: formatCount(ordersKpi.data?.totalItems),
      tone: 'warn',
      to: `/orders?orderStatus=${ORDERS_PENDING_QUERY.orderStatus}`,
      hint: t('dashboard.kpis.orders.hint'),
      testId: 'dashboard-kpi-orders',
      icon: <ShoppingBag className="h-4 w-4" />,
    },
    {
      label: t('dashboard.kpis.payments.label'),
      value: formatCount(paymentsKpi.data?.totalItems),
      tone: 'info',
      to: PAYMENTS_PENDING_LINK,
      hint: t('dashboard.kpis.payments.hint'),
      testId: 'dashboard-kpi-payments',
      icon: <CreditCard className="h-4 w-4" />,
    },
    {
      label: t('dashboard.kpis.claims.label'),
      value: formatCount(claimsKpi.data?.totalItems),
      tone: 'danger',
      to: `/claims?claimStatus=${CLAIMS_PENDING_QUERY.claimStatus}`,
      hint: t('dashboard.kpis.claims.hint'),
      testId: 'dashboard-kpi-claims',
      icon: <RefreshCw className="h-4 w-4" />,
    },
    ...(canViewFunding
      ? [
          {
            label: t('dashboard.kpis.funding.label'),
            value: formatCount(fundingKpi.data?.totalItems),
            tone: 'success' as const,
            to: '/funding-campaigns',
            hint: t('dashboard.kpis.funding.hint'),
            testId: 'dashboard-kpi-funding',
            icon: <HandCoins className="h-4 w-4" />,
          },
        ]
      : []),
  ];

  const wt = t('dashboard.widgets', { returnObjects: true }) as Record<string, Record<string, string>>;

  return (
    <PageShell
      title={t('sidebar.dashboard')}
      description={t('dashboard.description')}
      actions={
        <DashboardRangeFilter
          value={range}
          onChange={setRange}
          labels={{
            range7d: wt.revenue?.period7d ?? '7일',
            range14d: wt.revenue?.period14d ?? '14일',
            rangeAll: wt.revenue?.periodAll ?? '전체',
            ariaLabel: t('dashboard.rangeAria'),
          }}
        />
      }
      data-testid="dashboard-page"
    >
      {denied ? (
        <div
          className="rounded-2xl p-4 text-sm"
          role="alert"
          style={{
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-fg)',
            border: '1px solid var(--divider-soft)',
          }}
        >
          {t('dashboard.unauthorized')}
        </div>
      ) : null}

      {/* 처리 대기 액션 밴드 — 운영자가 바로 처리해야 하는 큐(가장 액션성 높음 → 최상단). */}
      <section data-testid="dashboard-action-band" aria-label={t('dashboard.kpiAria')}>
        <h2
          className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: 'var(--content-muted)' }}
        >
          {t('dashboard.actionBandTitle')}
        </h2>
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
          data-testid="dashboard-secondary-kpi-grid"
        >
          {secondaryKpis.map((k) => (
            <KpiSecondaryCard
              key={k.testId}
              label={k.label}
              value={k.value}
              tone={k.tone}
              {...(k.to ? { to: k.to } : {})}
              {...(k.hint ? { hint: k.hint } : {})}
              testId={k.testId}
              icon={k.icon}
            />
          ))}
        </div>
      </section>

      {/* Hero KPI 4 — 매출 / 주문 / 신규고객 / 재고부족 (비즈니스 추세). */}
      <section data-testid="dashboard-hero-band" aria-label={t('dashboard.heroKpiAria')}>
        <h2
          className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: 'var(--content-muted)' }}
        >
          {t('dashboard.heroBandTitle')}
        </h2>
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="dashboard-hero-kpi-grid"
        >
          {heroKpis.map((k) => (
            <KpiHeroCard
              key={k.testId}
              label={k.label}
              value={k.value}
              {...(k.unit ? { unit: k.unit } : {})}
              {...(typeof k.deltaPercent === 'number' ? { deltaPercent: k.deltaPercent } : {})}
              {...(k.deltaLabel ? { deltaLabel: k.deltaLabel } : {})}
              {...(k.sparkline && k.sparkline.length > 1 ? { sparkline: k.sparkline } : {})}
              tone={k.tone}
              {...(k.to ? { to: k.to } : {})}
              {...(k.hint ? { hint: k.hint } : {})}
              testId={k.testId}
              icon={k.icon}
            />
          ))}
        </div>
      </section>

      {/* Revenue Hero Panel + Channel Donut */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="dashboard-chart-row">
        <div className="lg:col-span-2">
          <RevenueHeroPanel
            {...(summary ? { summary } : {})}
            {...(widgetQuery.data?.revenueChart ? { chart: widgetQuery.data.revenueChart } : {})}
            isLoading={widgetQuery.isLoading}
            title={wt.revenue?.title ?? '매출 추이'}
            labels={{
              revenueTab: t('dashboard.heroPanel.revenueTab'),
              ordersTab: t('dashboard.heroPanel.ordersTab'),
              totalRevenue: t('dashboard.heroPanel.totalRevenue'),
              totalOrders: t('dashboard.heroPanel.totalOrders'),
              avgDailyRevenue: wt.revenue?.avgDailyRevenue ?? '일 평균',
              avgDailyOrders: t('dashboard.heroPanel.avgDailyOrders'),
              revenueTooltip: wt.revenue?.tooltipLabel ?? '매출',
              ordersTooltip: t('dashboard.heroPanel.ordersTooltip'),
              comparedToPrevDay: t('dashboard.heroPanel.comparedToPrevDay'),
            }}
          />
        </div>
        <div className="lg:col-span-1">
          <ChannelDonutChart
            data={widgetQuery.data?.channelBreakdown}
            isLoading={widgetQuery.isLoading}
            title={wt.channel?.title ?? '채널별 매출'}
            channelLabels={{
              DELIVERY: wt.channel?.delivery ?? '배송',
              PICKUP: wt.channel?.pickup ?? '픽업',
              WEB_STORE: wt.channel?.webStore ?? '웹스토어',
              MOBILE: wt.channel?.mobile ?? '모바일',
              B2B: wt.channel?.b2b ?? 'B2B',
              PARTNER: wt.channel?.partner ?? '파트너',
            }}
          />
        </div>
      </div>

      {/* Activity feed + Quick actions */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="dashboard-activity-row">
        <div className="lg:col-span-2">
          <ActivityFeed
            orders={widgetQuery.data?.recentOrders}
            isLoading={widgetQuery.isLoading}
            title={wt.recentOrders?.title ?? '실시간 주문 흐름'}
            labels={{
              viewAll: wt.recentOrders?.viewAll ?? '전체 보기',
              empty: wt.recentOrders?.empty ?? '최근 주문이 없습니다',
              emptyHint: t('dashboard.activity.emptyHint'),
              seeOrder: t('dashboard.activity.seeOrder'),
            }}
          />
        </div>
        <div className="lg:col-span-1">
          <QuickActionsPanel
            title={t('dashboard.quickActions.title')}
            description={t('dashboard.quickActions.description')}
            labels={{
              newProduct: t('dashboard.quickActions.newProduct'),
              newProductHint: t('dashboard.quickActions.newProductHint'),
              newCoupon: t('dashboard.quickActions.newCoupon'),
              newCouponHint: t('dashboard.quickActions.newCouponHint'),
              newEvent: t('dashboard.quickActions.newEvent'),
              newEventHint: t('dashboard.quickActions.newEventHint'),
              newNotice: t('dashboard.quickActions.newNotice'),
              newNoticeHint: t('dashboard.quickActions.newNoticeHint'),
              processClaim: t('dashboard.quickActions.processClaim'),
              processClaimHint: t('dashboard.quickActions.processClaimHint'),
            }}
          />
        </div>
      </div>

      {/* 하단 row: 재고 부족 + TOP5 */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="dashboard-bottom-row">
        <LowStockList
          items={widgetQuery.data?.lowStockAlerts}
          isLoading={widgetQuery.isLoading}
          title={wt.lowStock?.title ?? '재고 부족 상품'}
          labels={{
            product: wt.lowStock?.product ?? '상품',
            stock: wt.lowStock?.stock ?? '재고',
            viewAll: wt.lowStock?.viewAll ?? '전체 보기',
            empty: wt.lowStock?.empty ?? '재고 부족 상품이 없습니다',
          }}
        />
        <TopSellingList
          items={widgetQuery.data?.topSellingProducts}
          isLoading={widgetQuery.isLoading}
          title={wt.topSelling?.title ?? '판매 TOP 5'}
          labels={{
            rank: wt.topSelling?.rank ?? '순위',
            product: wt.topSelling?.product ?? '상품',
            sales: wt.topSelling?.sales ?? '판매',
            revenue: wt.topSelling?.revenue ?? '매출',
            viewAll: wt.topSelling?.viewAll ?? '전체 보기',
            empty: wt.topSelling?.empty ?? '판매 데이터가 없습니다',
          }}
        />
      </div>

    </PageShell>
  );
};
